import {
  QUALIFY_RAISING_WEIGHTS,
  RAISING_CODES,
  type QualifyResult,
  type RaisingCode,
} from "./qualifyLead";

/**
 * U3 QUALIFY (Agent Brains 5.3: "score + route; nobody rejected, everybody
 * routed"). The route and the why-now code of one `qualifyLead` result, and
 * the owner-facing line for each. Pure, codes only, wired to nothing that
 * gates a send: `lead.score` sits beside `reply.compose` (CLAUDE.md sections
 * 0 and 0.5, precision beats recall). Nothing here retunes a number or a list
 * in `qualifyLead.ts`; it reads them.
 *
 * VIP only ever routes to the carve-out (service level and routing, never a
 * number, CLAUDE.md section 3). The VIP input is the same `true | null` the
 * scorer is handed: `true` for a flagged contact, otherwise `null`, which is
 * the hold direction (TA-216 decision 1 is open, so a contact is never read as
 * known not VIP here).
 */

export const QUALIFY_ROUTES = ["priority", "standard", "later_lane", "vip_carve_out"] as const;
export type QualifyRoute = (typeof QUALIFY_ROUTES)[number];

export const WHY_NOW_CODES = [
  "intent_consult",
  "story_reply",
  "occasion_soon",
  "window_closing",
  "sla_breached",
  "need_and_timeline",
  "vip_carve_out",
  "no_signal_yet",
] as const;
export type WhyNowCode = (typeof WHY_NOW_CODES)[number];

/**
 * The largest raw score `qualifyLead` can return: every raising code present
 * at once, each counted once. Computed from the weights, never typed, so a
 * ratified retune moves it with them. `rawScore / QUALIFY_MAX_SCORE` is the
 * normalized sum in `lead.scored.score`: a share of the maximum, NOT a
 * probability.
 */
export const QUALIFY_MAX_SCORE: number = RAISING_CODES.reduce((sum, code) => sum + QUALIFY_RAISING_WEIGHTS[code], 0);

/**
 * `vip_carve_out` when the contact is flagged. Otherwise `priority` for a
 * result handled as priority, `later_lane` only for a result handled as low
 * signal (which `qualifyLead` yields only when the band is low and the lane is
 * open, so VIP is known false: unreachable while the shipped lowering
 * catalogue is empty), else `standard` (an unknown band is handled as standard).
 */
export function routeOf(result: QualifyResult, vipFlag: true | null): QualifyRoute {
  if (vipFlag === true) return "vip_carve_out";
  if (result.handledAs === "priority") return "priority";
  if (result.handledAs === "low_signal") return "later_lane";
  return "standard";
}

const WHY_OF_CODE: Readonly<Record<RaisingCode, WhyNowCode>> = Object.freeze({
  "intent.consult": "intent_consult",
  "intent.visit": "intent_consult",
  "intent.call": "intent_consult",
  "intent.kind_not_read": "intent_consult",
  "entry.story_reply": "story_reply",
  "urgency.window_open": "window_closing",
  "urgency.window_grace": "window_closing",
  "urgency.sla_breached": "sla_breached",
  "urgency.occasion_within_30d": "occasion_soon",
  "need.evidenced": "need_and_timeline",
  "timeline.evidenced": "need_and_timeline",
});

/**
 * The why-now of the highest-weight raising code present, ties broken by the
 * order of `RAISING_CODES` (deterministic). A flagged contact reads
 * `vip_carve_out`, since that is why the thread is routed as it is. No raising
 * code reads `no_signal_yet`: unknown is not assumed.
 */
export function whyNowOf(result: QualifyResult, vipFlag: true | null): WhyNowCode {
  if (vipFlag === true) return "vip_carve_out";
  let best: RaisingCode | null = null;
  for (const code of RAISING_CODES) {
    if (!result.raising.includes(code)) continue;
    if (best === null || QUALIFY_RAISING_WEIGHTS[code] > QUALIFY_RAISING_WEIGHTS[best]) best = code;
  }
  return best === null ? "no_signal_yet" : WHY_OF_CODE[best];
}

/**
 * One owner-facing line per why-now code, for the call brief's lead-quality
 * seam. Never sent to a customer. Offer-free and wealth-free (CLAUDE.md
 * section 0.5), pinned by `fixedLinesNoOffer.test.ts`.
 */
export const WHY_NOW_LINE: Readonly<Record<WhyNowCode, string>> = Object.freeze({
  intent_consult: "Asked to come in, book or be called.",
  story_reply: "Opened by replying to a story.",
  occasion_soon: "A dated occasion is within 30 days.",
  window_closing: "The reply window is open and will close.",
  sla_breached: "Waiting past the shop's own reply time.",
  need_and_timeline: "Has said what they are after and when.",
  vip_carve_out: "VIP flag set, routed for personal service.",
  no_signal_yet: "No signal yet, routed like everyone else.",
});
